import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, ArrowLeft, Image } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("brgt_token");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const CATEGORIES = ["Insights", "Technology", "Case Study", "Company News", "Research"];

export default function AdminBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const editorRef = useRef(null);
  const [ReactQuill, setReactQuill] = useState(null);

  const [form, setForm] = useState({
    title: "", content: "", excerpt: "", category: "Insights",
    cover_image: "", author: "BRGT Team", status: "draft", tags: [],
  });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  // Dynamic import of ReactQuill
  useEffect(() => {
    import("react-quill").then((mod) => {
      setReactQuill(() => mod.default);
    });
    import("react-quill/dist/quill.snow.css");
  }, []);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/blogs`, authHeaders()).then(({ data }) => {
        const blog = data.find(b => b.id === id);
        if (blog) {
          setForm({
            title: blog.title, content: blog.content, excerpt: blog.excerpt,
            category: blog.category, cover_image: blog.cover_image,
            author: blog.author, status: blog.status, tags: blog.tags || [],
          });
        }
      }).catch(() => {});
    }
  }, [id, isEdit]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) {
      setForm({ ...form, tags: [...form.tags, t] });
    }
    setTagInput("");
  };

  const removeTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  const handleSave = async (status) => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = { ...form, status };
    try {
      if (isEdit) {
        await axios.put(`${API}/blogs/${id}`, payload, authHeaders());
        toast.success("Post updated!");
      } else {
        await axios.post(`${API}/blogs`, payload, authHeaders());
        toast.success("Post created!");
      }
      navigate("/admin/blogs");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'link'],
      ['clean'],
    ],
  };

  return (
    <div data-testid="admin-blog-editor">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/blogs")}
            data-testid="back-to-blogs"
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft size={18} className="text-slate-400" />
          </button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-navy" data-testid="editor-title">
              {isEdit ? "Edit Post" : "New Post"}
            </h1>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleSave("draft")}
            data-testid="save-draft-btn"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-white text-navy border border-slate-200 hover:bg-slate-50 rounded-full px-5 py-2 text-sm font-semibold transition-all disabled:opacity-50"
          >
            Save Draft
          </button>
          <button
            onClick={() => handleSave("published")}
            data-testid="publish-btn"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-navy text-white hover:bg-navy-light rounded-full px-5 py-2 text-sm font-semibold transition-all disabled:opacity-50"
          >
            <Save size={14} /> Publish
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main Editor */}
        <div className="lg:col-span-8 space-y-5">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <label className="block text-sm font-medium text-navy mb-2">Title</label>
            <input
              type="text"
              data-testid="blog-title-input"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Enter post title..."
              className="w-full text-xl font-heading font-semibold bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none transition-all"
            />
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <label className="block text-sm font-medium text-navy mb-2">Content</label>
            {ReactQuill ? (
              <ReactQuill
                ref={editorRef}
                value={form.content}
                onChange={(val) => setForm({ ...form, content: val })}
                modules={quillModules}
                theme="snow"
                placeholder="Write your post content..."
                data-testid="blog-content-editor"
              />
            ) : (
              <div className="h-48 bg-slate-50 rounded-lg animate-pulse" />
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <label className="block text-sm font-medium text-navy mb-2">Excerpt</label>
            <textarea
              data-testid="blog-excerpt-input"
              value={form.excerpt}
              onChange={e => setForm({ ...form, excerpt: e.target.value })}
              rows={3}
              placeholder="Brief summary for blog listing..."
              className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none transition-all resize-none text-sm"
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-5">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <label className="block text-sm font-medium text-navy mb-2">Category</label>
            <select
              data-testid="blog-category-select"
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none transition-all text-sm"
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <label className="block text-sm font-medium text-navy mb-2">Cover Image URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                data-testid="blog-cover-input"
                value={form.cover_image}
                onChange={e => setForm({ ...form, cover_image: e.target.value })}
                placeholder="https://..."
                className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none transition-all text-sm"
              />
            </div>
            {form.cover_image && (
              <img src={form.cover_image} alt="Preview" className="mt-3 rounded-lg w-full h-32 object-cover border border-slate-100" />
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <label className="block text-sm font-medium text-navy mb-2">Author</label>
            <input
              type="text"
              data-testid="blog-author-input"
              value={form.author}
              onChange={e => setForm({ ...form, author: e.target.value })}
              className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none transition-all text-sm"
            />
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <label className="block text-sm font-medium text-navy mb-2">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                data-testid="blog-tag-input"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Add tag..."
                className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none transition-all text-sm"
              />
              <button
                onClick={addTag}
                data-testid="add-tag-btn"
                className="bg-slate-100 hover:bg-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-navy transition-colors"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {form.tags.map(tag => (
                <span key={tag} className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full text-xs flex items-center gap-1">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-red-500">&times;</button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
